<template>
  <div>
    <form-field-input
      label="Name"
      v-model="presetForm.label"
      :validation-errors="validationErrors.label"
    />

    <div class="flex flex-vertical mb-100" v-if="isPresetOwner">
      <form-field-switch
        label="Is Private"
        v-model="presetForm.user_id"
        :true-value="LocalStorage.getAuthUser().id"
        :false-value="null"
        :validation-errors="validationErrors.user_id"
        class="mb-0"
      />
      <p class="help is-black mt-50">If off this preset will be available for all users.</p>
    </div>

    <div class="preset_columns">
      <div class="preset_columns--column" v-if="!hideColumns">
        <h4 class="preset_columns--heading">Columns</h4>
        <div class="mb-50" v-for="(column, index) in presetForm.columns">
          <b-switch :value="true" v-model="column.visible" size="is-small" :key="index" expanded>{{ column.label }}</b-switch>
        </div>
      </div>

      <div class="preset_columns--column">
        <h4 class="preset_columns--heading">Filters</h4>
        <leads-filters
          v-if="storeModule === 'leads'"
          filter-name="preset"
        />

        <contacts-filter
          v-else-if="storeModule === 'contacts'"
          store-module="contacts"
          filter-name="preset"
        />

        <inbox-filters
          v-else-if="storeModule === 'inbox'"
          filter-name="preset"
        />
      </div>
    </div>

    <div class="preset_footer">
      <template v-if="presetForm.id">
        <button-link
          class="mr-auto"
          color="is-danger"
          :loading="deleting"
          :disabled="saving"
          @click="deletePreset(presetForm.id)"
        >
          Delete
        </button-link>

        <button-default :loading="saving" @click="updatePreset">
          Save
        </button-default>
      </template>
      <template v-else>
        <button-default :loading="saving" @click="savePreset">
          Save
        </button-default>
      </template>
    </div>
  </div>
</template>

<script>
import {ENUMS} from "../services/enums";
import api from "../api";
import LeadsFilters from "../modules/leads/components/LeadsFilters.vue";
import ContactsFilter from "../modules/contacts/components/ContactsFilters.vue";
import LocalStorage from "../services/localStorage";
import InboxFilters from "../modules/inbox/components/InboxFilters.vue";

export default {
  name: "PresetForm",
  components: {InboxFilters, ContactsFilter, LeadsFilters},
  props: {
    storeModule: {
      type: String,
      required: true
    },
    presetId: {
      type: Number,
      default: 0
    },
    hideColumns: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      saving: false,
      deleting: false,
      validationErrors: {},
      presetForm: {
        id: null,
        account_id: this.$store.getters.account.id,
        user_id: null,
        location: null,
        label: null,
        columns: [],
        filters: []
      },
    }
  },
  created() {
    if (!this.hideColumns) {
      this.$store.dispatch(`${this.storeModule}/loadCustomFieldsColumns`, "preset")
        .then(() => {
          this.setupPreset()
        })
    } else {
      this.setupPreset()
    }
  },
  mounted() {
    const locationEnum = ENUMS.TableConfigurationLocationEnum
    if (this.storeModule === 'leads') this.presetForm.location = locationEnum.Record.value

    if (this.storeModule === 'contacts') this.presetForm.location = locationEnum.Contact.value

    if (this.storeModule === 'inbox') this.presetForm.location = locationEnum.Inbox.value
  },
  computed: {
    LocalStorage() {
      return LocalStorage
    },
    presets() {
      if (this.storeModule === 'leads') return this.$store.getters.leadsTableConfigurations

      if (this.storeModule === 'contacts') return this.$store.getters.contactsTableConfigurations

      if (this.storeModule === 'inbox') return this.$store.getters.inboxTableConfigurations

      return []
    },
    isPresetOwner() {
      const preset = this.presets.find(preset => preset.id === this.presetId)

      if (! preset) return true;

      return preset.owner_id === LocalStorage.getAuthUser().id
    }
  },
  methods: {
    setupPreset() {
      this.resetPresetForm()

      this.presetForm.filters = this.$store.getters[`${this.storeModule}/filters`]("preset")
      if (!this.hideColumns) {
        this.presetForm.columns = this.$store.getters[`${this.storeModule}/columns`]("preset")
      }

      this.handlePresetId()
    },
    handlePresetId() {
      const preset = this.presets.find(preset => preset.id === this.presetId)

      if (! preset) return

      this.presetForm.id = preset.id
      this.presetForm.label = preset.label
      this.presetForm.user_id = preset.user_id

      if (!this.hideColumns) {
        for (const [columnKey, columnValue] of Object.entries(this.presetForm.columns)) {
          const columnPreset = preset.columns.find(preset => preset.field === columnValue.field)
          if (columnPreset) {
            this.$store.commit(`${this.storeModule}/setOneColumn`, ["preset", columnKey, columnPreset.visible])
          }
        }
      }

      for (const [filterKey, filterValue] of Object.entries(this.presetForm.filters)) {
        const filterPresetValue = preset.filters[filterKey]
        if (filterPresetValue) {
          this.$store.commit(`${this.storeModule}/setOneFilter`, ["preset", filterKey, filterPresetValue])
        }
      }
    },
    resetPresetForm() {
      this.validationErrors = {}
      this.presetForm.id = null
      this.presetForm.label = null

      this.$store.commit(`${this.storeModule}/resetFilters`, "preset")
      if (!this.hideColumns) {
        this.$store.commit(`${this.storeModule}/resetColumns`, "preset")
      }
    },
    savePreset() {
      this.saving = true
      api.tableConfiguration.create(this.presetForm)
        .then(res => {
          this.$buefy.toast.open({
            message: 'Preset was saved',
            type: 'is-success'
          })

          this.resetPresetForm()
        })
        .catch((error) => {
          if (error.response.status === 422) this.validationErrors = error.response.data.errors
        })
        .finally(() => this.saving = false)
    },
    updatePreset() {
      this.saving = true
      api.tableConfiguration.update(this.presetForm)
        .then(res => {
          this.$buefy.toast.open({
            message: 'Preset was updated',
            type: 'is-success'
          })
        })
        .catch((error) => {
          if (error.response.status === 422) this.validationErrors = error.response.data.errors
        })
        .finally(() => this.saving = false)
    },
    deletePreset(presetId) {
      this.deleting = true
      api.tableConfiguration.delete(presetId)
        .then((res) => {
          this.$emit('deleted')

          this.$buefy.toast.open({
            message: 'Preset was deleted',
            type: 'is-success'
          })
        })
        .finally(() => this.deleting = false)
    },
  },
};
</script>

<style scoped lang="scss">
@import "../styles/variables/variables";

.preset_columns {
  display: flex;
  margin-bottom: 14px;
  border-bottom: 1px solid $border;

  &--heading {
    font-size: 0.875rem;
    margin-bottom: 8px;
  }

  &--column {
    flex-grow: 1;

    &:first-of-type {
      padding-right: 20px;
    }

    &:last-of-type {
      padding-left: 10px;
      margin-left: -20px;
      border-left: 1px solid $border;
    }

    &:only-child {
      padding-right: 0;
      padding-left: 0;
      margin-left: 0;
      border-left: none;
    }
  }
}

.preset_footer {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;

  .button:not(:last-child) {
    margin-right: 0.5em;
  }
}
</style>
